import { useFormik } from 'formik';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import {
  GetCompatibleDocument,
  GetCompatibleQuery,
  GetCompatibleQueryVariables,
  GetPreApplicationStatusDocument,
  GetPreApplicationStatusQuery,
  GetPreApplicationStatusQueryVariables,
  useApplySymbolMutation,
  useConfirmApplySymbolMutation,
  useGetDomesticRealEstateSymbolInfoQuery,
  DomesticRealEstateSymbolDetailInfo,
  useGetContractInfoQuery,
  GetDateTimeQuery,
  GetDateTimeQueryVariables,
  GetDateTimeDocument,
} from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import * as Yup from 'yup';
import { Client, Context as UrqlContext } from 'urql';
import { ApplySymbolRequestType, ApplyAssetType } from 'views/utils/enums';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { useGetApplicationAmountQuery } from 'views/utils/graphql/graphql';
import { useGetDateTimeQuery } from 'views/utils/graphql/graphql';
import { ContractStatus } from 'constants/contract';

export enum SymbolPageStep {
  Detail,
  CompatibilityCheck,
  ApplicationStatus,
  Confirm,
  PendingAdjustment,
  ContractComplete,
  ContractCanceled,
}

export type ApplySymbolInput = {
  symbol: string;
  amount: number | null;
  isCheck1: boolean;
  isCheck2: boolean;
  isCheck3: boolean;
  isCheck4: boolean;
  applicationStartDateTime: string;
};

const transferDateRangeError = (transferStartDate: string, transferEndDate: string) => {
  return `${transferStartDate}～${transferEndDate}の範囲にしてください`;
};

const transferDateSchema = (
  transferStartDate: string | null | undefined,
  transferEndDate: string | null | undefined,
) => {
  // 入力制御しているが念のため
  const base = Yup.date().typeError('日付で入力してください').required('必須です');
  if (transferStartDate && transferEndDate) {
    return base
      .min(new Date(transferStartDate), transferDateRangeError(transferStartDate, transferEndDate))
      .max(new Date(transferEndDate), transferDateRangeError(transferStartDate, transferEndDate));
  }

  return base;
};

const validation = Yup.object().shape({
  symbol: Yup.string().required(),
  isCheck1: Yup.boolean().isTrue(),
  isCheck2: Yup.boolean().isTrue(),
  isCheck3: Yup.boolean().isTrue(),
  isCheck4: Yup.boolean().isTrue(),
});

const limitDateCheck = (today: Date, limitDate: Date | null): boolean => {
  if (!limitDate) return false;

  const addDays = 1;
  limitDate.setDate(limitDate.getDate() + addDays);

  return today < limitDate;
};
// falseの場合は条件を満たしていないので申し込めない状態
const canApplySymbol = (symbol: DomesticRealEstateSymbolDetailInfo | null | undefined, input: ApplySymbolInput) => {
  if (
    symbol &&
    // apiのレスポンスとして申し込めるかのフラグがある
    input.amount &&
    input.isCheck1 &&
    input.isCheck2 &&
    input.isCheck3 &&
    input.isCheck4
  ) {
    return true;
  } else {
    return false;
  }
};

const GetErrorText = (amount: number | null, tradable?: boolean | null) => {
  if (!tradable) {
    return 'こちらの案件は現在お申し込みいただけません';
  } else if (!amount) {
    return '申込個数が取得できませんでした';
  } else {
    return;
  }
};

type SymbolPageProps = {
  symbol: string;
  id: string;
};

export const useDomesticRealEstateSymbolPage = ({ symbol, id }: SymbolPageProps) => {
  const [symbolPageStep, setSymbolPageStep] = useState<SymbolPageStep | null>(null);
  const [symbolDetailInfo, _reload] = useGetDomesticRealEstateSymbolInfoQuery({
    variables: {
      symbol,
    },
    requestPolicy: 'network-only',
  });
  const [applySymbolRes, applySymbol] = useApplySymbolMutation();
  const [confirmApplySymbolRes, confirmApplySymbol] = useConfirmApplySymbolMutation();
  const { showError, messageClose } = useGlobalToastHandler();
  const applyOnlyCodeList = [411, 424, 426, 427];
  const backStartCodeList = [422];

  const [ApplicationAmountInfo, _] = useGetApplicationAmountQuery({
    variables: {
      id: id,
    },
    requestPolicy: 'network-only',
  });
  const [dateTime, getDataTime] = useGetDateTimeQuery({
    requestPolicy: 'network-only',
  });
  const [contractInfo, getContractInfo] = useGetContractInfoQuery({
    variables: {
      id,
    },
    requestPolicy: 'network-only',
  });
  const client = useContext(UrqlContext) as Client;
  const [limitDateResult, setLimitDateResult] = useState<boolean>(false);

  const onSubmit = useOnceSubmit<ApplySymbolInput>(
    {
      onPreviousSubmit: async (input) => {
        return canApplySymbol(symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo, input);
      },
      onSubmit: async (input) => {
        switch (symbolPageStep) {
          case SymbolPageStep.Detail: {
            // ConfirmでAPIのエラー → Detailに戻って修正 → Confirmに遷移した場合はエラーが消えるようにする
            messageClose();

            getDataTime();

            //配当金・fatca・インボイス回答チェック
            const dividendAccountRes = await client.query<
              GetPreApplicationStatusQuery,
              GetPreApplicationStatusQueryVariables
            >(
              GetPreApplicationStatusDocument,
              {},
              {
                requestPolicy: 'network-only',
              },
            );

            if (
              !(
                dividendAccountRes.data?.GetAccountInfo?.ApplicationStatus?.HasAnsweredDividendAccount &&
                dividendAccountRes.data?.GetAccountInfo?.ApplicationStatus?.HasValidFatcaAnswer != null &&
                dividendAccountRes.data?.GetAccountInfo?.ApplicationStatus?.HasAnsweredInvoiceNumber
              )
            ) {
              setSymbolPageStep(SymbolPageStep.ApplicationStatus);
              return;
            }
            const res = await confirmApplySymbol({
              input: {
                Symbol: input.symbol!,
                ApplicationID: id,
                Amount: input.amount!,
                // TransferDate: formatDate(input.transferDate, DateFormatType.Slash_yyyyMMdd),
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Confirm,
                AssetType: ApplyAssetType.DomesticRealEstate,
                ApplicationStartDate: input.applicationStartDateTime,
                ResaleFlag: true,
              },
            });

            if (!res?.data) return;
            if (res.data.ApplySymbol?.Code === 0 || applyOnlyCodeList.includes(res.data.ApplySymbol?.Code ?? -1)) {
              //undefinedの時はfalseになるように-1 を入れる
              setSymbolPageStep(SymbolPageStep.Confirm);
            } else {
              setSymbolPageStep(SymbolPageStep.Detail);
              showError(res.data.ApplySymbol?.Message);
            }
            break;
          }
          case SymbolPageStep.CompatibilityCheck: {
            // ConfirmでAPIのエラー → Detailに戻って修正 → Confirmに遷移した場合はエラーが消えるようにする
            messageClose();

            //配当金・fatca・インボイス回答チェック
            const dividendAccountRes = await client.query<
              GetPreApplicationStatusQuery,
              GetPreApplicationStatusQueryVariables
            >(
              GetPreApplicationStatusDocument,
              {},
              {
                requestPolicy: 'network-only',
              },
            );

            if (
              !(
                dividendAccountRes.data?.GetAccountInfo?.ApplicationStatus?.HasAnsweredDividendAccount &&
                dividendAccountRes.data?.GetAccountInfo?.ApplicationStatus?.HasValidFatcaAnswer != null &&
                dividendAccountRes.data?.GetAccountInfo?.ApplicationStatus?.HasAnsweredInvoiceNumber
              )
            ) {
              setSymbolPageStep(SymbolPageStep.ApplicationStatus);
              return;
            }
            const res = await confirmApplySymbol({
              input: {
                Symbol: input.symbol!,
                ApplicationID: id,
                Amount: input.amount!,
                // TransferDate: formatDate(input.transferDate, DateFormatType.Slash_yyyyMMdd),
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Confirm,
                AssetType: ApplyAssetType.DomesticRealEstate,
                ApplicationStartDate: input.applicationStartDateTime,
                ResaleFlag: true,
              },
            });

            if (!res?.data) return;
            if (res.data.ApplySymbol?.Code === 0 || applyOnlyCodeList.includes(res.data.ApplySymbol?.Code ?? -1)) {
              //undefinedの時はfalseになるように-1 を入れる
              setSymbolPageStep(SymbolPageStep.Confirm);
            } else {
              setSymbolPageStep(SymbolPageStep.Detail);
              showError(res.data.ApplySymbol?.Message);
            }
            break;
          }
          case SymbolPageStep.ApplicationStatus: {
            // ConfirmでAPIのエラー → Detailに戻って修正 → Confirmに遷移した場合はエラーが消えるようにする
            messageClose();

            const res = await confirmApplySymbol({
              input: {
                Symbol: input.symbol!,
                ApplicationID: id,
                Amount: input.amount!,
                // TransferDate: formatDate(input.transferDate, DateFormatType.Slash_yyyyMMdd),
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Confirm,
                AssetType: ApplyAssetType.DomesticRealEstate,
                ApplicationStartDate: input.applicationStartDateTime,
                ResaleFlag: true,
              },
            });

            if (!res?.data) return;
            if (res.data.ApplySymbol?.Code === 0 || applyOnlyCodeList.includes(res.data.ApplySymbol?.Code ?? -1)) {
              //undefinedの時はfalseになるように-1 を入れる
              setSymbolPageStep(SymbolPageStep.Confirm);
            } else {
              setSymbolPageStep(SymbolPageStep.Detail);
              showError(res.data.ApplySymbol?.Message);
            }
            break;
          }
          case SymbolPageStep.Confirm: {
            // 適合性チェックが済んでいない場合は適合性チェックを表示する
            const compatibleRes = await client.query<GetCompatibleQuery, GetCompatibleQueryVariables>(
              GetCompatibleDocument,
              {},
              {
                requestPolicy: 'network-only',
              },
            );

            if (!compatibleRes.data?.GetAccountInfo?.Compatible?.DomesticRealEstate) {
              setSymbolPageStep(SymbolPageStep.CompatibilityCheck);
              return;
            }

            //オンライン期日チェック
            const dateTimeRes = await client.query<GetDateTimeQuery, GetDateTimeQueryVariables>(
              GetDateTimeDocument,
              {},
              {
                requestPolicy: 'network-only',
              },
            );

            const isLimitDate = limitDateCheck(
              new Date(dateTimeRes.data?.GetDateTime?.Current!),
              symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo?.ContractURLLimitDate
                ? new Date(symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo.ContractURLLimitDate)
                : null,
            );

            if (!isLimitDate) {
              setSymbolPageStep(SymbolPageStep.Detail);
              showError('契約期日を過ぎています。');
              return;
            }

            const appSymbolRes = await applySymbol({
              input: {
                Symbol: input.symbol!,
                ApplicationID: id,
                Amount: input.amount!,
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Apply,
                AssetType: ApplyAssetType.DomesticRealEstate,
                ApplicationStartDate: input.applicationStartDateTime,
              },
            });
            if (!appSymbolRes?.data) return;
            if (appSymbolRes.data.ApplySymbol?.Code === 0) {
              setSymbolPageStep(SymbolPageStep.ContractComplete);
            } else if (applyOnlyCodeList.includes(appSymbolRes.data.ApplySymbol?.Code ?? -1)) {
              //undefinedの時はfalseになるように-1 を入れる
              setSymbolPageStep(SymbolPageStep.PendingAdjustment);
            } else if (backStartCodeList.includes(appSymbolRes.data.ApplySymbol?.Code ?? -1)) {
              showError(appSymbolRes.data.ApplySymbol?.Message);
              setSymbolPageStep(SymbolPageStep.Detail);
            } else {
              showError(appSymbolRes.data.ApplySymbol?.Message);
            }
            break;
          }
          case SymbolPageStep.PendingAdjustment:
          case SymbolPageStep.ContractComplete:
            break;
        }
      },
    },
    [symbolPageStep, setSymbolPageStep, applySymbol, showError, messageClose],
  );

  const backDetailPage = useCallback(() => {
    switch (symbolPageStep) {
      case SymbolPageStep.Confirm: {
        setSymbolPageStep(SymbolPageStep.Detail);
        break;
      }
      case SymbolPageStep.Detail:
      case SymbolPageStep.CompatibilityCheck:
      case SymbolPageStep.ApplicationStatus:
      case SymbolPageStep.PendingAdjustment:
      case SymbolPageStep.ContractComplete:
        break;
    }
  }, [symbolPageStep, setSymbolPageStep]);

  const formik = useFormik<ApplySymbolInput>({
    initialValues: {
      symbol,
      amount: ApplicationAmountInfo.data?.GetApplication?.ApplicationAmount ?? null,
      isCheck1: false,
      isCheck2: false,
      isCheck3: false,
      isCheck4: false,
      applicationStartDateTime: '',
    },
    onSubmit,
    validationSchema: validation,
    validateOnBlur: true,
    validateOnChange: true,
  });

  useEffect(() => {
    formik.setFieldValue('amount', ApplicationAmountInfo.data?.GetApplication?.ApplicationAmount);
  }, [ApplicationAmountInfo.data?.GetApplication?.ApplicationAmount]);

  useEffect(() => {
    if (!dateTime.data?.GetDateTime) return;
    formik.values.applicationStartDateTime = dateTime.data?.GetDateTime?.Current!;
  }, [dateTime]);

  const check1Props = {
    checked: formik.values.isCheck1,
    onChange: () => formik.setFieldValue('isCheck1', !formik.values.isCheck1),
  };

  const check2Props = {
    checked: formik.values.isCheck2,
    onChange: () => formik.setFieldValue('isCheck2', !formik.values.isCheck2),
  };

  const check3Props = {
    checked: formik.values.isCheck3,
    onChange: () => formik.setFieldValue('isCheck3', !formik.values.isCheck3),
  };

  const check4Props = {
    checked: formik.values.isCheck4,
    onChange: () => formik.setFieldValue('isCheck4', !formik.values.isCheck4),
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  useEffect(() => {
    if (!contractInfo.data?.GetContractInfo || symbolPageStep !== null) return;

    // 契約状態からStepを設定するマッピング
    const contractStatusToStepMap: Partial<Record<ContractStatus, SymbolPageStep>> = {
      [ContractStatus.Previous]: SymbolPageStep.Detail,
      [ContractStatus.Completed]: SymbolPageStep.ContractComplete,
      [ContractStatus.Canceled]: SymbolPageStep.ContractCanceled,
    };

    const status = contractInfo.data.GetContractInfo.Details?.Status;

    // ステータスが有効で、マッピングに存在する場合のみ処理
    if (status != null && status in contractStatusToStepMap) {
      const targetStep = contractStatusToStepMap[status as ContractStatus]!;
      setSymbolPageStep(targetStep);
    }
  }, [contractInfo.data?.GetContractInfo, setSymbolPageStep]);

  useEffect(() => {
    //契約期限チェック
    setLimitDateResult(
      limitDateCheck(
        new Date(formik.values.applicationStartDateTime),
        symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo?.ContractURLLimitDate
          ? new Date(symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo.ContractURLLimitDate)
          : null,
      ),
    );
  }, [symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo?.ContractURLLimitDate]);

  return {
    symbolPageStep,
    symbolDetailInfo,
    ApplicationAmountInfo,
    applySymbolRes,
    confirmApplySymbolRes,
    formik,
    canApplySymbol:
      canApplySymbol(symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo, formik.values) && limitDateResult,
    impossobleDetailErrorText: GetErrorText(formik.values.amount, limitDateResult),
    check1Props,
    check2Props,
    check3Props,
    check4Props,
    backDetailPage,
    contractInfo,
  };
};
